import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n/hooks.ts";
import { kindLabelKey } from "../i18n/fieldLabels.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import { DropdownMenu } from "./DropdownMenu.tsx";
import { SearchIcon } from "./SearchIcon.tsx";

const MAX_RESULTS = 20;

function allElements(project: SwmmProject): ElementRef[] {
  return [
    ...project.raingages.map((g): ElementRef => ({
      category: "raingage",
      kind: "raingage",
      name: g.name,
    })),
    ...project.subcatchments.map((s): ElementRef => ({
      category: "subcatchment",
      kind: "subcatchment",
      name: s.name,
    })),
    ...project.nodes.map((n): ElementRef => ({ category: "node", kind: n.kind, name: n.name })),
    ...project.links.map((l): ElementRef => ({ category: "link", kind: l.kind, name: l.name })),
  ];
}

export function ElementSearch({
  project,
  open,
  onToggle,
  onClose,
  onSelect,
}: {
  project: SwmmProject;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelect: (ref: ElementRef) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  // Which result the keyboard is currently on — Up/Down move it, Enter picks
  // it (falling back to the first result when nothing's been highlighted
  // yet, same as before this fix). Same pattern as ProjectionPicker.tsx's
  // own EPSG-search combobox.
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlightedIndex(null);
      inputRef.current?.focus();
    }
  }, [open]);

  const elements = useMemo(() => allElements(project), [project]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return elements.filter((ref) => ref.name.toLowerCase().includes(q)).slice(0, MAX_RESULTS);
  }, [elements, query]);

  function pick(ref: ElementRef) {
    onSelect(ref);
    onClose();
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((current) =>
        current === null ? 0 : Math.min(current + 1, matches.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((current) =>
        current === null ? matches.length - 1 : Math.max(current - 1, 0)
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(matches[highlightedIndex ?? 0]);
    }
  }

  return (
    <DropdownMenu
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      triggerClassName="button-icon topbar-icon-button"
      triggerLabel={t("toolbar.search")}
      triggerIcon={<SearchIcon />}
      popoverClassName="popover element-search-popover"
      closeOnLeave={false}
    >
      <input
        ref={inputRef}
        type="text"
        className="element-search-input"
        placeholder={t("toolbar.searchPlaceholder")}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlightedIndex(null);
        }}
        onKeyDown={handleInputKeyDown}
        role="combobox"
        aria-expanded={query.trim() !== ""}
        aria-controls="element-search-listbox"
        aria-autocomplete="list"
        aria-activedescendant={
          highlightedIndex !== null ? `element-search-option-${highlightedIndex}` : undefined
        }
      />
      {query.trim() !== "" && (
        <ul className="element-search-results" role="listbox" id="element-search-listbox">
          {matches.length === 0 ? (
            <li className="element-search-empty">{t("toolbar.searchNoMatches")}</li>
          ) : (
            matches.map((ref, index) => {
              const kindKey = kindLabelKey(ref.kind);
              return (
                <li key={`${ref.category}-${ref.name}`} role="presentation">
                  <button
                    id={`element-search-option-${index}`}
                    className="button"
                    role="option"
                    aria-selected={index === highlightedIndex}
                    onClick={() => pick(ref)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span>{ref.name}</span>
                    {kindKey && <span className="element-search-kind">{t(kindKey)}</span>}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </DropdownMenu>
  );
}
